import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { TaskStatusSchema } from '@erp/rpc';

export const inboxSearchSchema = z.object({
  tab: z.enum(['inbox', 'tasks', 'agents']).optional(),
  search: z.string().optional(),
  filter: z.enum(['all', 'escalations', 'notifications']).optional(),
  escalationFilter: z
    .enum(['all', 'needs-input', 'approvals', 'failures'])
    .optional(),
  dateFrom: z.iso.date().optional(),
  dateTo: z.iso.date().optional(),
  // Inverted: the inbox shows read notifications by default, so we only persist
  // the non-default "hide read" state. `setParams` strips falsy values, so a
  // default-on `showRead` couldn't round-trip its off state — `hideRead` only
  // ever stores `true`.
  hideRead: z.boolean().optional(),
  selId: z.string().optional(),
  // --- Tasks workspace filters ---
  // Multi-select status. Absent = all statuses (no filter).
  taskStatuses: z.array(TaskStatusSchema).optional(),
  // Assignee scope: 'mine' (default) | 'watching' | 'all' | 'user:<id>' |
  // 'agent:<id>'. Stripped from the URL when 'mine'.
  assigneeFilter: z.string().optional(),
  // Namespace id; absent = all namespaces. `taskListId` is a deprecated alias
  // so old inbox URLs still filter after the rename.
  namespaceId: z.string().optional(),
  taskListId: z.string().optional(),
  // Inclusive [from, to] date range (filtered against the task's updated date).
  taskDateFrom: z.iso.date().optional(),
  taskDateTo: z.iso.date().optional(),
  taskSearch: z.string().optional(),
  // `setParams` strips `false`, so this only round-trips an explicit on.
  // A non-empty task search includes snoozed tasks without storing this.
  showSnoozed: z.boolean().optional(),
  taskId: z.string().optional(),
  agentId: z.string().optional(),
});

export type InboxSearchParams = z.infer<typeof inboxSearchSchema>;

export function useInboxSearchParams() {
  const params = useSearch({ strict: false }) as InboxSearchParams;
  const navigate = useNavigate();

  const setParams = useCallback(
    (updates: Partial<InboxSearchParams>) => {
      navigate({
        to: '.',
        search: ((prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev, ...updates };
          for (const [k, v] of Object.entries(next)) {
            if (v === undefined || v === null || v === '' || v === false)
              delete next[k];
          }
          return next;
        }) as never,
        replace: true,
      });
    },
    [navigate],
  );

  return { params, setParams };
}
